import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

const layoutTransition = { type: 'spring' as const, stiffness: 350, damping: 30 }

interface PlaybookDetailSkeletonProps {
  slug?: string
}

export function PlaybookDetailSkeleton({ slug }: PlaybookDetailSkeletonProps) {
  return (
    <div className="space-y-6" role="status" aria-label="Loading playbook">
      {/* Main content card – matches PlaybookContentCard */}
      <motion.div layoutId={slug ? `playbook-card-${slug}` : undefined} transition={layoutTransition}>
        <Card variant="elevated">
          <CardContent className="pt-8 pb-8 px-6 lg:px-12">
            <div className="flex flex-col items-center text-center space-y-5">
              <Skeleton className="h-6 w-32 rounded-full" />
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-72" />
              <div className="relative w-full max-w-md">
                <motion.div
                  className="w-full aspect-video rounded-xl overflow-hidden"
                  layoutId={slug ? `playbook-image-${slug}` : undefined}
                  transition={layoutTransition}
                >
                  <Skeleton className="w-full h-full" />
                </motion.div>
              </div>
              <div className="w-full max-w-lg space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Coach Grace promo – matches CoachGracePromoCard */}
      <div
        className="relative rounded-2xl overflow-hidden px-6 lg:px-12 py-8"
        style={{
          background: 'linear-gradient(110deg, hsl(var(--surface-subtle)) 7%, hsl(var(--background)) 100%)',
        }}
      >
        <div className="flex flex-col md:flex-row items-center gap-6">
          <Skeleton className="shrink-0 w-48 md:w-52 h-64 rounded-lg" />
          <div className="flex-1 space-y-3 w-full">
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </div>

      {/* Doer steps – matches DoerStepsCard */}
      <Card variant="elevated">
        <CardContent className="pt-8 pb-8 px-6">
          <div className="text-center mb-8">
            <Skeleton className="h-6 w-56 mx-auto mb-2" />
            <Skeleton className="h-4 w-80 mx-auto" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="text-center space-y-2">
                <Skeleton className="h-12 w-10 mx-auto" />
                <Skeleton className="h-4 w-24 mx-auto" />
                <Skeleton className="h-3 w-32 mx-auto" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
